import { useState, useEffect, useRef, useCallback } from 'react';
import { saveDraft } from '../lib/api';

const AUTO_SAVE_INTERVAL = 30_000; // 30 seconds

interface UseDraftReturn {
  draftSavedAt: string | null;
  isDirty: boolean;
  markDirty: () => void;
  markClean: () => void;
  forceSave: (path: string, content: string) => Promise<void>;
}

export function useDraft(
  path: string | null,
  getMarkdown: () => string
): UseDraftReturn {
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pathRef = useRef<string | null>(path);
  const isSavingRef = useRef(false);

  // Keep pathRef in sync
  useEffect(() => {
    pathRef.current = path;
  }, [path]);

  const doSave = useCallback(async () => {
    const currentPath = pathRef.current;
    if (!currentPath || isSavingRef.current) return;

    const markdown = getMarkdown();
    if (!markdown.trim()) return;

    isSavingRef.current = true;
    try {
      const result = await saveDraft(currentPath, markdown);
      setDraftSavedAt(result.savedAt);
      setIsDirty(false);
    } catch (err) {
      console.warn('Auto-save failed:', err);
    } finally {
      isSavingRef.current = false;
    }
  }, [getMarkdown]);

  // Set up auto-save interval
  useEffect(() => {
    if (saveTimerRef.current) {
      clearInterval(saveTimerRef.current);
    }

    if (path) {
      saveTimerRef.current = setInterval(() => {
        if (isDirty) {
          doSave();
        }
      }, AUTO_SAVE_INTERVAL);
    }

    return () => {
      if (saveTimerRef.current) {
        clearInterval(saveTimerRef.current);
      }
    };
  }, [path, isDirty, doSave]);

  const markDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  const markClean = useCallback(() => {
    setIsDirty(false);
    setDraftSavedAt(new Date().toISOString());
  }, []);

  const forceSave = useCallback(
    async (savePath: string, content: string) => {
      try {
        const result = await saveDraft(savePath, content);
        setDraftSavedAt(result.savedAt);
        setIsDirty(false);
      } catch (err) {
        console.warn('Force save failed:', err);
      }
    },
    []
  );

  return { draftSavedAt, isDirty, markDirty, markClean, forceSave };
}
